import { useDropzone } from 'react-dropzone'
import { Input } from './input'
import { useIsMobile } from '@/hooks/use-mobile'
import { FileIcon, ImageIcon } from 'lucide-react'
import { MIME_TYPE_CONFIG } from '@/data/mime-types'
import { cn } from '@/lib/utils'

type DropzoneProps = {
  disabled: boolean
  onDrop: (acceptedFiles: File[]) => void
  accept?: Record<string, string[]>
  className?: string
}

export function Dropzone({
  disabled,
  onDrop,
  accept,
  className,
}: DropzoneProps) {
  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    onDrop,
    disabled,
    accept,
  })
  const isMobile = useIsMobile()

  return (
    <section
      className={cn(
        'flex h-40 w-full min-w-0 flex-col items-center justify-center gap-4 rounded-xl border border-dashed p-4',
        className,
      )}
    >
      <div
        {...getRootProps({
          className:
            'dropzone flex h-full w-full min-w-0 flex-col justify-center items-center',
        })}
        role="button"
        tabIndex={0}
      >
        <Input {...getInputProps()} />

        {acceptedFiles.length > 0 ? (
          <div className="flex h-full w-full flex-col gap-1">
            <p className="text-copy-secondary text-sm">Selected files:</p>
            <ul className="flex w-full flex-1 flex-col gap-2 overflow-y-auto">
              {acceptedFiles.map((file) => (
                <li
                  key={file.name}
                  className="flex w-full flex-row items-center gap-1"
                >
                  <div>
                    {MIME_TYPE_CONFIG[file.type]?.icon || (
                      <FileIcon className="size-4" />
                    )}
                  </div>
                  <p className="text-copy-primary grow truncate text-sm">
                    {file.name}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <>
            {isMobile ? <ImageIcon className="text-primary m-2" /> : null}
            <p className="text-copy-secondary w-full text-center hover:underline hover:underline-offset-2">
              {isMobile
                ? 'Tap here to select files or take a photo'
                : 'Drag and drop files or click to select files'}
            </p>
          </>
        )}
      </div>
    </section>
  )
}
